import {AfterViewInit, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {PageModel, TableConfiguration} from "../../../tandem-core/models/table-configuration";
import {AuthService} from "../../../auth/services/auth.service";
import {HeaderService} from "../../../tandem-core/services/header.service";
import {ActivatedRoute, Router} from "@angular/router";
import {DateService} from "../../../tandem-core/services/date.service";
import {CurrencyService} from "../../../tandem-core/services/currency.service";
import {DialogService} from "../../../tandem-core/services/dialog.service";
import {CashFlow} from "../../models/cash-flow";
import {CashFlowService} from "../../services/cash-flow.service";
import {ThemingService} from "../../../tandem-core/services/theming.service";
import {CashFlowChartMetadata} from "../../models/cash-flow-chart-metadata";
import {CashFlowChartMetadataService} from "../../services/cash-flow-chart-metadata.service";
import {BehaviorSubject, combineLatest, startWith, Subject, switchMap, takeUntil} from "rxjs";
import {EChartsOption} from "echarts";
import {ChartService} from "../../../tandem-core/services/chart.service";
import {TandemUser} from "../../../auth/models/tandem-user";
import {VideoPlayerComponent} from "../../../tandem-core/components/video-player/video-player.component";
import {DatePipe} from "@angular/common";
import {TandemDatePipe} from "../../../tandem-core/directives/tandem-date.pipe";
import {UserService} from "../../../auth/user.service";

@Component({
  selector: 'tandem-cash-flow-list',
  templateUrl: './cash-flow-list.component.html',
  styleUrls: ['./cash-flow-list.component.css']
})
export class CashFlowListComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() reviewMode: boolean = false;

  findStatementForm: FormGroup = new FormGroup<any>({});
  isStatementVisible: FormControl = new FormControl({value: true, disabled: false});
  chartData: CashFlowChartMetadata | null = null;

  tableConfig: TableConfiguration<CashFlow> = {
    title: undefined,
    noDataMessage: 'No Cash Flows found.',
    onRowClick: t => {this.router.navigate([`cash-flows/${t.id}`])},
    editRowFunction: t => {this.router.navigate([`cash-flows/${t.id}/edit`])},
    deleteRowFunction: t => {this.onDelete(t)},
    columns: [
      {
        title: 'For Period',
        sortable: true,
        itemProperty: "startDate",
        columnType: "text",
        displayMask: t => this.getTitle(t),
        sortedByDefault: true
      },
      {
        title: 'Cash Flow',
        sortable: true,
        itemProperty: "netCashFlow",
        columnType: "value",
        displayMask: t => this.currencyService.formatNumber(t.netCashFlow)
      },
      {
        title: 'Last Modified',
        sortable: true,
        itemProperty: "dateModified",
        displayMask: t => {return this.datePipe.transform(t.dateModified.toDate())},
        columnType: "text"
      },
    ],
    paginated: true,
    sortColumn: 'startDate',
    sortDirection: 'desc',
    pageSize: 5
  };
  data: CashFlow[] = [];

  tablePageModel$: BehaviorSubject<PageModel<CashFlow> | undefined> = new BehaviorSubject<PageModel<CashFlow> | undefined>({sortOrder: "desc", sortProperty: "startDate"});
  reviewedUserId?: string;

  chartOptions: EChartsOption = {};
  user: TandemUser | null = null;
  usingSampleData = false;

  @ViewChild('customContent', {static: false})
  public customContent?: TemplateRef<any>;
  private destroy$ = new Subject<void>();


  constructor(private auth: AuthService,
              private fb: FormBuilder,
              private cashFlowService: CashFlowService,
              private headerService: HeaderService,
              private router: Router,
              private route: ActivatedRoute,
              private dateService: DateService,
              private currencyService: CurrencyService,
              private themingService: ThemingService,
              private metadataService: CashFlowChartMetadataService,
              private dialogService: DialogService,
              private chartService: ChartService,
              private userService: UserService,
              private datePipe: TandemDatePipe) {
  }

  ngOnInit(): void {
    this.findStatementForm = this.fb.group({
      startDate: null,
      endDate: null,
    });
    if (this.reviewMode) {
      this.tableConfig.onRowClick = t => {this.router.navigate([`${this.reviewMode ? 'coaching/user-management/' + this.reviewedUserId + '/' : ''}cash-flows/${t.id}`])}
      this.tableConfig.editRowFunction = undefined;
      this.tableConfig.deleteRowFunction = undefined;
      this.reviewedUserId = this.route.snapshot.paramMap.get('userId') || '';
    }


    combineLatest([
      this.auth.$user,
      this.findStatementForm.valueChanges.pipe(startWith(this.findStatementForm.value)),
      this.tablePageModel$.pipe(startWith({sortProperty: 'startDate', sortOrder: 'desc'}))
    ]).pipe(
      takeUntil(this.destroy$),
      switchMap(([user, formValues, pageModel]) => {
        this.user = user;
        if (!this.reviewMode) {
          this.headerService.setConfig({
            title: this.user?.customTTMetadata?.cashFlowNamePlural ? this.user.customTTMetadata.cashFlowNamePlural : 'Cash Flows',
            subtitle: this.getTrackingToolDescription(),
            breadcrumbs: [{label: this.user?.customTTMetadata?.cashFlowNamePlural ? this.user.customTTMetadata.cashFlowNamePlural : 'Cash Flows'}],
            optionalTemplate: this.customContent
          });
        }
        if (user) {
          this.metadataService.listByUserId(this.reviewedUserId ? this.reviewedUserId : user.uid).subscribe(chartData => {
            if (chartData && chartData[0]?.charts?.length > 1) {
              this.usingSampleData = false;
              this.renderChart(chartData[0])
            } else {
              this.usingSampleData = true;
              this.renderChart(this.metadataService.generateSampleChartData(user.uid));
            }
          });
          const startDate = formValues.startDate ? new Date(formValues.startDate) : undefined;
          const endDate = formValues.endDate ? new Date(formValues.endDate) : undefined;
          return this.cashFlowService.listTrackingToolsByDateRange(this.reviewedUserId ? this.reviewedUserId : user.uid, 'startDate', startDate, endDate, pageModel as PageModel<CashFlow>);
        }
        return [];
      })
    ).pipe(takeUntil(this.destroy$)).subscribe(cashFlows => {
      this.data = [...cashFlows];
      if (this.user?.promptCFVideo) {
        this.showVideo();
        this.user.promptCFVideo = false;
        this.userService.update(this.user)
      }
    });
    this.themingService.selectedTheme.subscribe(newTheme => {
      if (this.chartData) {
        this.renderChart(this.chartData);
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.customContent) {
      this.headerService.setTemplateRef(this.customContent);
    }
  }

  ngOnDestroy(): void {
    // Signal all subscriptions to complete
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPageUpdate(pageModel: PageModel<CashFlow>) {
    this.tablePageModel$.next(pageModel);
  }

  onDelete(statement: CashFlow) {
    this.cashFlowService.delete(statement.id!).then(res => this.dialogService.showAlert({
      title: 'Successfully Deleted Cash Flow',
      position: 'bottom',
      content: `You successfully deleted your statement '${statement.title}'`,
      dismissTime: 5000
    }));
  }

  onCreateNew(mostRecent: boolean = false) {
    this.router.navigate([`cash-flows/${mostRecent ? 'create/most-recent' : 'create'}`]);
  }

  private renderChart(chartData: CashFlowChartMetadata) {
    chartData.charts = chartData.charts.sort((a, b) => {
      if (a.startDate && b.startDate) {
        let dateA = a.startDate.toDate().getTime();
        let dateB = b.startDate.toDate().getTime();

        return dateA - dateB;
      } else {
        return 0;
      }
    });
    this.chartData = chartData;
    let labels: string[] = chartData.charts
      .map(chart => chart.startDate.toDate())
      .map(date => `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`);
    let netCashFlowData: number[] = chartData.charts.map(d => d.netCashFlow);
    let revenueData: number[] = chartData.charts.map(d => d.totalRevenue);
    let pyfData: number[] = chartData.charts.map(d => d.totalPYFExpenses);
    let essentialData: number[] = chartData.charts.map(d => d.totalEssentialExpenses);
    let lifestyleData: number[] = chartData.charts.map(d => d.totalLifestyleExpenses);

    let businessData: number[] | null = null;

    if (chartData.charts.find(c => c.totalBusinessExpenses !== null)) {
      businessData = chartData.charts.map(d => d.totalBusinessExpenses!);
    }

    if (businessData) {
      this.chartOptions = this.chartService.generateLineChart('Date', 'Value', labels, [
        {name: 'Net Cash Flow', data: netCashFlowData, color: `${this.themingService.getPrimaryRGB()}`},
        {name: 'Revenue', data: revenueData, color: `#4CAF50`, disableByDefault: true},
        {name: 'Pay Yourself First Expenses', data: pyfData, color: `#f1c232`, disableByDefault: true},
        {name: 'Business Expenses', data: businessData, color: `#2a4d84`, disableByDefault: true},
        {name: 'Essential Expenses', data: essentialData, color: `#e69138`, disableByDefault: true},
        {name: 'Lifestyle Expenses', data: lifestyleData, color: `#F44336`, disableByDefault: true},
      ]);
    } else {
      this.chartOptions = this.chartService.generateLineChart('Date', 'Value', labels, [
        {name: 'Net Cash Flow', data: netCashFlowData, color: `${this.themingService.getPrimaryRGB()}`},
        {name: 'Revenue', data: revenueData, color: `#4CAF50`, disableByDefault: true},
        {name: 'Pay Yourself First Expenses', data: pyfData, color: `#f1c232`, disableByDefault: true},
        {name: 'Essential Expenses', data: essentialData, color: `#e69138`, disableByDefault: true},
        {name: 'Lifestyle Expenses', data: lifestyleData, color: `#F44336`, disableByDefault: true},
      ]);
    }
  }

  showVideo() {
    this.dialogService.openModal(VideoPlayerComponent, {videoType: 'cashFlow'}, {}, 'sm:w-[75vw]');
  }

  private getTitle(t: CashFlow) {
    return `${this.formatDateForMonthYear(this.dateService.getAdjustedTimestampFromDate(t.startDate.toDate()).toDate())} to ${this.formatDateForMonthYear(this.dateService.getAdjustedTimestampFromDate(t.endDate.toDate()).toDate())}`
  }
  formatDateForMonthYear(date: Date) {
    let formatted = this.datePipe.transform(date, 'MMM dd, yyyy');
    if (formatted) {
      return formatted;
    }
    return date.toDateString();
  }
  private formatDate(date: Date) {
    let day: string | number = date.getDate();
    let month: string | number = date.getMonth() + 1; // January is 0!
    let year = date.getFullYear();

    // ensure double digits
    day = day < 10 ? '0' + day : day;
    month = month < 10 ? '0' + month : month;

    return month + '/' + day + '/' + year;
  }

  getTrackingToolName(variant: 'single' | 'plural') {
    if (variant === 'single') {
      return this.user?.customTTMetadata?.cashFlowName ? this.user.customTTMetadata.cashFlowName : 'Cash Flow';
    } else {
      return this.user?.customTTMetadata?.cashFlowNamePlural ? `${this.user.customTTMetadata.cashFlowNamePlural}` : 'Cash Flows';
    }
  }

  getTrackingToolDescription() {
    return !!this.user?.customTTMetadata?.cashFlowDescription ? this.user.customTTMetadata.cashFlowDescription : 'A summary of your cash coming in and going out over a period of time.'
  }

  resetForm() {
    this.findStatementForm.reset();
  }
}
